<template>
    <DashboardLayout title="Settings" subtitle="Company profile and workspace users.">
        <div class="settings-view">
            <div class="settings-grid">
                <section class="panel">
                    <div class="toolbar compact">
                        <h2>Company</h2>
                        <span v-if="companySaved" class="badge">Saved</span>
                    </div>
                    <form class="form-grid" @submit.prevent="saveCompany">
                        <div v-if="companyError" class="error">{{ companyError }}</div>
                        <div class="settings-logo-row">
                            <img v-if="company.logoUrl" class="settings-logo" :src="company.logoUrl" alt="" />
                            <span v-else class="settings-logo placeholder">{{ companyInitials }}</span>
                            <div>
                                <label class="file-button">
                                    Upload logo
                                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" @change="uploadCompanyLogo" />
                                </label>
                                <p class="muted-text">{{ company.logoFileName || "PNG, JPEG, WebP or GIF up to 5 MB." }}</p>
                            </div>
                        </div>
                        <div class="field">
                            <label>Company Name</label>
                            <input v-model="company.name" required />
                        </div>
                        <button class="primary-button" :disabled="companyLoading">
                            {{ companyLoading ? "Saving..." : "Save company" }}
                        </button>
                    </form>
                </section>

                <section class="panel">
                    <div class="toolbar compact">
                        <h2>My Profile</h2>
                        <span v-if="profileSaved" class="badge">Saved</span>
                    </div>
                    <form class="form-grid" @submit.prevent="saveProfile">
                        <div v-if="profileError" class="error">{{ profileError }}</div>
                        <div class="settings-logo-row">
                            <img v-if="me.avatarUrl" class="settings-avatar" :src="me.avatarUrl" alt="" />
                            <span v-else class="settings-avatar placeholder">{{ myInitials }}</span>
                            <div>
                                <label class="file-button">
                                    Upload photo
                                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" @change="uploadMyAvatar" />
                                </label>
                                <p class="muted-text">{{ me.avatarFileName || "Shown in the dashboard menu." }}</p>
                            </div>
                        </div>
                        <div class="field">
                            <label>Full Name</label>
                            <input v-model="me.fullName" required />
                        </div>
                        <button class="primary-button" :disabled="profileLoading">
                            {{ profileLoading ? "Saving..." : "Save profile" }}
                        </button>
                    </form>
                </section>
            </div>

            <section class="panel settings-section">
                <div class="toolbar compact">
                    <h2>Users</h2>
                </div>
                <form class="settings-user-form" @submit.prevent="createUser">
                    <div v-if="userError" class="error form-wide">{{ userError }}</div>
                    <div class="field">
                        <label>Full Name</label>
                        <input v-model="newUser.fullName" required />
                    </div>
                    <div class="field">
                        <label>Email</label>
                        <input v-model="newUser.email" type="email" required />
                    </div>
                    <div class="field">
                        <label>User Name</label>
                        <input v-model="newUser.userName" required autocomplete="username" />
                    </div>
                    <div class="field">
                        <label>Password</label>
                        <input v-model="newUser.password" type="password" minlength="8" required autocomplete="new-password" />
                    </div>
                    <div class="field">
                        <label>Role</label>
                        <select v-model="newUser.role">
                            <option value="Member">Member</option>
                            <option value="Admin">Admin</option>
                        </select>
                    </div>
                    <button class="primary-button" :disabled="userLoading">
                        {{ userLoading ? "Adding..." : "Add user" }}
                    </button>
                </form>
                <div class="table-container settings-table">
                    <table class="table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="user in users" :key="user.id">
                                <td>
                                    <div class="user-cell">
                                        <img v-if="user.avatarUrl" class="row-avatar" :src="user.avatarUrl" alt="" />
                                        <span v-else class="row-avatar">{{ initials(user.fullName || user.userName || user.email) }}</span>
                                        <div>
                                            <strong>{{ user.fullName || user.userName || user.email }}</strong>
                                            <small>{{ user.userName }}</small>
                                        </div>
                                    </div>
                                </td>
                                <td>{{ user.email }}</td>
                                <td>
                                    <span class="badge">{{ user.role }}</span>
                                </td>
                                <td>{{ formatDate(user.createdAt) }}</td>
                            </tr>
                            <tr v-if="!users.length">
                                <td colspan="4">
                                    <StateBlock title="No users yet" message="Create the first additional workspace user above." />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    </DashboardLayout>
</template>

<script>
import DashboardLayout from "../layouts/DashboardLayout.vue";
import StateBlock from "../components/StateBlock.vue";
import {api} from "../services/api";
import {useAuthStore} from "../stores/auth";

export default {
    name: "SettingsView",
    components: {
        DashboardLayout,
        StateBlock,
    },
    data() {
        return {
            auth: useAuthStore(),
            company: {id: "", name: "", logoUrl: "", logoFileName: ""},
            me: {fullName: "", avatarUrl: "", avatarFileName: ""},
            users: [],
            companyLoading: false,
            profileLoading: false,
            userLoading: false,
            companySaved: false,
            profileSaved: false,
            companyError: "",
            profileError: "",
            userError: "",
            newUser: {fullName: "", email: "", userName: "", password: "", role: "Member"},
        };
    },
    computed: {
        companyInitials() {
            return this.initials(this.company.name || this.auth.user?.companyName || "EA");
        },
        myInitials() {
            return this.initials(this.me.fullName || this.auth.user?.fullName || this.auth.user?.email || "U");
        },
    },
    mounted() {
        this.loadSettings();
    },
    methods: {
        async loadSettings() {
            const [{data: companyData}, {data: usersData}] = await Promise.all([api.get("/settings/company"), api.get("/settings/users")]);
            this.setCompany(companyData);
            this.users = usersData;
            const currentUser = usersData.find((user) => user.id === this.auth.user?.id);
            this.me.fullName = currentUser?.fullName || this.auth.user?.fullName || "";
            this.me.avatarUrl = currentUser?.avatarUrl || this.auth.user?.avatarUrl || "";
            this.me.avatarFileName = currentUser?.avatarFileName || "";
        },

        async saveCompany() {
            this.companyLoading = true;
            this.companyError = "";
            this.companySaved = false;
            try {
                const {data} = await api.put("/settings/company", {name: this.company.name});
                this.setCompany(data);
                await this.auth.loadMe();
                this.companySaved = true;
            } catch (err) {
                this.companyError = this.resolveError(err, "Company could not be saved.");
            } finally {
                this.companyLoading = false;
            }
        },

        async uploadCompanyLogo(event) {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file) return;
            this.companyError = "";
            const formData = new FormData();
            formData.append("logo", file);
            try {
                const {data} = await api.post("/settings/company/logo", formData, {
                    headers: {"Content-Type": "multipart/form-data"},
                });
                this.setCompany(data);
                await this.auth.loadMe();
            } catch (err) {
                this.companyError = this.resolveError(err, "Logo could not be uploaded.");
            }
        },

        async saveProfile() {
            this.profileLoading = true;
            this.profileError = "";
            this.profileSaved = false;
            try {
                await api.put("/settings/users/me", {fullName: this.me.fullName});
                await this.refreshUsersAndMe();
                this.profileSaved = true;
            } catch (err) {
                this.profileError = this.resolveError(err, "Profile could not be saved.");
            } finally {
                this.profileLoading = false;
            }
        },

        async uploadMyAvatar(event) {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file) return;
            this.profileError = "";
            const formData = new FormData();
            formData.append("avatar", file);
            try {
                await api.post("/settings/users/me/avatar", formData, {
                    headers: {"Content-Type": "multipart/form-data"},
                });
                await this.refreshUsersAndMe();
            } catch (err) {
                this.profileError = this.resolveError(err, "Photo could not be uploaded.");
            }
        },

        async createUser() {
            this.userLoading = true;
            this.userError = "";
            try {
                await api.post("/settings/users", this.newUser);
                Object.assign(this.newUser, {fullName: "", email: "", userName: "", password: "", role: "Member"});
                await this.refreshUsersAndMe();
            } catch (err) {
                this.userError = this.resolveError(err, "User could not be added.");
            } finally {
                this.userLoading = false;
            }
        },

        async refreshUsersAndMe() {
            const [{data: usersData}] = await Promise.all([api.get("/settings/users"), this.auth.loadMe()]);
            this.users = usersData;
            const currentUser = usersData.find((user) => user.id === this.auth.user?.id);
            this.me.fullName = currentUser?.fullName || this.auth.user?.fullName || "";
            this.me.avatarUrl = currentUser?.avatarUrl || this.auth.user?.avatarUrl || "";
            this.me.avatarFileName = currentUser?.avatarFileName || "";
        },

        setCompany(data) {
            this.company.id = data.id;
            this.company.name = data.name || "";
            this.company.logoUrl = data.logoUrl || "";
            this.company.logoFileName = data.logoFileName || "";
        },

        initials(value) {
            return (
                String(value || "")
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((part) => part[0]?.toUpperCase())
                    .join("") || "EA"
            );
        },

        formatDate(value) {
            return value ? new Intl.DateTimeFormat("tr-TR", {dateStyle: "medium"}).format(new Date(value)) : "";
        },

        resolveError(err, fallback) {
            return err.response?.data?.error || err.response?.data?.title || err.response?.data || fallback;
        },
    },
};
</script>

<style scoped>
.settings-view {
    display: grid;
    gap: 1.25rem;
    padding: 0.25rem;
}

.settings-grid {
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    padding: unset;
}

.panel {
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 16px;
    padding: 1rem;
}

.toolbar.compact {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.85rem;
}

.toolbar.compact h2 {
    margin: 0;
    font-size: 1rem;
    letter-spacing: 0.01em;
}

.settings-logo-row {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    margin-bottom: 0.35rem;
}

.settings-logo,
.settings-avatar {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    object-fit: cover;
    border: 1px solid rgba(15, 23, 42, 0.1);
    background: #f8fafc;
    flex-shrink: 0;
}

.settings-avatar {
    border-radius: 50%;
}

.placeholder {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    color: #0f172a;
    background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
}

.form-grid,
.settings-user-form {
    display: grid;
    gap: 0.75rem;
}

.settings-user-form {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    align-items: end;
}

.form-wide {
    grid-column: 1 / -1;
}

.field {
    display: grid;
    gap: 0.35rem;
}

.field label {
    font-size: 0.78rem;
    font-weight: 700;
    color: #475569;
    letter-spacing: 0.02em;
    text-transform: uppercase;
}

.field input,
.field select {
    width: 100%;
    border-radius: 10px;
    border: 1px solid rgba(148, 163, 184, 0.55);
    background: #fff;
    padding: 0.62rem 0.7rem;
    color: #0f172a;
    transition:
        border-color 0.2s ease,
        box-shadow 0.2s ease;
}

.field input:focus,
.field select:focus {
    outline: 0;
    border-color: #0ea5e9;
    box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.12);
}

.file-button {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    border: 1px dashed rgba(2, 132, 199, 0.45);
    background: rgba(14, 165, 233, 0.08);
    color: #0369a1;
    font-weight: 700;
    border-radius: 10px;
    padding: 0.42rem 0.68rem;
    cursor: pointer;
}

.file-button input[type="file"] {
    display: none;
}

.muted-text {
    margin: 0.4rem 0 0;
    color: #64748b;
    font-size: 0.8rem;
}

.settings-table {
    margin-top: 0.8rem;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 12px;
    overflow: hidden;
}

.table {
    width: 100%;
    border-collapse: collapse;
}

.table thead {
    background: rgba(241, 245, 249, 0.8);
}

.table th,
.table td {
    text-align: left;
    padding: 0.75rem;
    border-bottom: 1px solid rgba(148, 163, 184, 0.2);
}

.table tbody tr:last-child td {
    border-bottom: none;
}

.user-cell {
    display: flex;
    align-items: center;
    gap: 0.55rem;
}

.row-avatar {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    object-fit: cover;
    background: #e2e8f0;
    color: #0f172a;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.72rem;
    font-weight: 700;
    border: 1px solid rgba(148, 163, 184, 0.45);
}

.user-cell strong {
    display: block;
    color: #0f172a;
    font-size: 0.9rem;
}

.user-cell small {
    color: #64748b;
}

.badge {
    background: rgba(16, 185, 129, 0.12);
    color: #047857;
    border: 1px solid rgba(16, 185, 129, 0.25);
    border-radius: 999px;
    padding: 0.2rem 0.52rem;
    font-size: 0.76rem;
    font-weight: 700;
}

.settings-section {
    padding-top: 1.05rem;
}

@media (max-width: 768px) {
    .settings-view {
        gap: 1rem;
    }

    .panel {
        padding: 0.85rem;
        border-radius: 14px;
    }

    .table th,
    .table td {
        padding: 0.6rem;
    }
}
</style>
